import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';

import moment from 'moment';

import { DateParserService }      from '../../../core/services/date-parser.service';
import { LEGACY_DATE }            from './legacy-date.constant';
import { LegacyDateKindModel }    from './typings/legacy-date.kind';
import { LegacyDateOptionsModel } from './typings/legacy-date.options';
import { LegacyDateParseModel }   from './typings/legacy-date.parse';
import { LegacyDatePickerModel }  from './typings/legacy-date.picker';

@Injectable({
  providedIn: 'root'
})
export class LegacyDateService {
  constructor (
    private dateParserService: DateParserService
  ) {}

  private _isDayFirst () : boolean {
    return this.dateParserService.getFormat() === LEGACY_DATE.dayFirstFormat;
  }

  // A year alone is read only when it holds 19 or 20, as in AngularJS.
  private _monthPattern ( text : string ) : RegExp {
    const isYear = !!text && !text.includes('/') && text.length === 4 && ( text.includes('19') || text.includes('20') );

    return isYear
      ? LEGACY_DATE.yearPattern
      : LEGACY_DATE.monthPattern;
  }

  private _parseDate ( text : string, options : LegacyDateOptionsModel ) : LegacyDateParseModel {
    const blank = options.isFilter
      ? LEGACY_DATE.allDates
      : '';
    const match = text.match(LEGACY_DATE.viewPattern);

    if ( !text ) {
      return { isValid: true, value: blank };
    }

    if ( !match ) {
      return { isValid: false, value: blank };
    }

    const isDayFirst = this._isDayFirst();

    // An impossible date rolls over in Date and fails the read-back check below.
    const date  = this._toDate(text, LEGACY_DATE.viewPattern);
    const day   = parseInt(isDayFirst ? match[ 1 ] : match[ 2 ], 10);
    const month = parseInt(isDayFirst ? match[ 2 ] : match[ 1 ], 10);

    if ( date.getDate() !== day || date.getMonth() + 1 !== month || date.getFullYear() !== parseInt(match[ 3 ], 10) ) {
      return { isValid: false, text, value: blank };
    }

    if ( options.startDate && this.pickerStartDate(options.startDate) > date ) {
      return { isValid: false, text, value: blank };
    }

    return { isValid: true, text, value: this._toModel(date, options.modelFormat) };
  }

  // The checks copy AngularJS, so 0/2019 passes, and parseInt keeps its default radix as there.
  private _parseMonth ( text : string ) : LegacyDateParseModel {
    const latestYear = new Date().getFullYear() + 1;
    const pattern    = this._monthPattern(text);

    if ( !text ) {
      return { isValid: true, value: '' };
    }

    if ( !pattern.test(text) ) {
      return { isValid: false, value: undefined };
    }

    const first   = parseInt(text.replace(pattern, '$1'));
    const isValid = text.length === 4
      ? first <= latestYear
      : first <= 12 && parseInt(text.replace(pattern, '$2')) <= latestYear;

    return isValid
      ? { isValid, text, value: text }
      : { isValid, value: undefined };
  }

  // No impossible-date check here, so 31/02 rolls into March as it does in AngularJS.
  private _parseRolledOver ( text : string, kind : LegacyDateKindModel ) : LegacyDateParseModel {
    const isJob   = kind === 'job-date';
    const pattern = isJob
      ? LEGACY_DATE.looseViewPattern
      : LEGACY_DATE.viewPattern;

    if ( !text ) {
      return { isValid: true, text, value: LEGACY_DATE.allDates };
    }

    if ( !pattern.test(text) ) {
      return { isValid: false, value: isJob ? undefined : '' };
    }

    const date  = this._toDate(text, pattern);
    const day   = date.getDate();
    const month = date.getMonth() + 1;
    const year  = date.getFullYear();

    return {
      isValid: true,
      text,
      value  : isJob
        ? `${ year }-${ month }-${ day }`
        : `${ day }-${ month }-${ year }`
    };
  }

  private _toDate ( text : string, pattern : RegExp ) : Date {
    return new Date(this._isDayFirst() ? text.replace(pattern, '$2/$1/$3') : text);
  }

  private _toModel ( date : Date, modelFormat : string ) : string {
    if ( modelFormat === 'iso' ) {
      return date.toISOString();
    }

    return formatDate(date, modelFormat === 'date' ? 'yyyy-MM-dd' : modelFormat, 'en-US');
  }

  // A model value with a time part is not a match, so it shows blank.
  format ( value : string, kind : LegacyDateKindModel ) : string {
    if ( this.isMonth(kind) ) {
      return typeof value === 'string' && value && this._monthPattern(value).test(value)
        ? value
        : '';
    }

    const match = typeof value === 'string'
      ? value.replace(/-/g, '/').match(LEGACY_DATE.modelPattern)
      : null;

    if ( !match ) {
      return '';
    }

    return this._isDayFirst()
      ? `${ match[ 3 ] }/${ match[ 2 ] }/${ match[ 1 ] }`
      : `${ match[ 2 ] }/${ match[ 3 ] }/${ match[ 1 ] }`;
  }

  // The plugin can hand back the day before across a daylight-saving change; only the date kind corrects it.
  formatPick ( picked : Date, kind : LegacyDateKindModel ) : string {
    // In month view the plugin can hand back the last day of the month before; a week on is always the picked month.
    if ( this.isMonth(kind) ) {
      return moment(picked).add(LEGACY_DATE.monthPickOffsetDay, 'days').format(LEGACY_DATE.monthPickFormat);
    }

    const date = new Date(picked.getTime() + ( this.isRolledOver(kind) ? 0 : LEGACY_DATE.pickOffset ));

    return this.dateParserService.getFormat()
      .replace('dd', `${ date.getDate() }`)
      .replace('mm', `${ date.getMonth() + 1 }`)
      .replace('yyyy', `${ date.getFullYear() }`);
  }

  // AngularJS trims the text first, so spaces alone leave a field empty.
  isBlank ( typed : string ) : boolean {
    return !( typed || '' ).trim();
  }

  // Any other value reads as the date kind.
  isDate ( kind : LegacyDateKindModel ) : boolean {
    return !this.isMonth(kind) && !this.isRolledOver(kind);
  }

  isMonth ( kind : LegacyDateKindModel ) : boolean {
    return kind === 'short-date';
  }

  // The kinds that read dates as the AngularJS report and job fields do.
  isRolledOver ( kind : LegacyDateKindModel ) : boolean {
    return kind === 'job-date' || kind === 'report-date';
  }

  // Spaces around the text are ignored, as AngularJS trims typed text before reading it.
  parse ( typed : string, options : LegacyDateOptionsModel ) : LegacyDateParseModel {
    const text = ( typed || '' ).trim();

    if ( this.isMonth(options.kind) ) {
      return this._parseMonth(text);
    }

    return this.isRolledOver(options.kind)
      ? this._parseRolledOver(text, options.kind)
      : this._parseDate(text, options);
  }

  pickerOptions ( startDate : string, kind : LegacyDateKindModel ) : LegacyDatePickerModel {
    if ( this.isMonth(kind) ) {
      return {
        ...LEGACY_DATE.pickerOptions,
        ...LEGACY_DATE.monthPickerOptions,
        startDate: ''
      };
    }

    return {
      ...LEGACY_DATE.pickerOptions,
      format   : this.dateParserService.getFormat(),
      startDate: this.pickerStartDate(startDate)
    };
  }

  pickerStartDate ( startDate : string ) : Date | string {
    return startDate
      ? moment(startDate).toDate()
      : '';
  }
}
